import { fetchAuthSession } from "aws-amplify/auth";

// Server uses app.setGlobalPrefix("api") (see server/src/main.ts).
// Set VITE_API_BASE_URL to "https://api.resume-x.app/api".
const ensureApiPrefix = (baseUrl: string): string => {
  const normalized = baseUrl.trim().replace(/\/+$/, "");
  if (!normalized) return "";
  return /\/api$/i.test(normalized) ? normalized : `${normalized}/api`;
};

const isLocalHostname = (hostname: string): boolean =>
  hostname === "localhost" ||
  hostname === "127.0.0.1" ||
  hostname === "0.0.0.0";

const resolveApiBaseUrl = (): string => {
  const configuredBase = import.meta.env.VITE_API_BASE_URL?.trim() ?? "";

  if (typeof window === "undefined") {
    return configuredBase.length > 0 ? ensureApiPrefix(configuredBase) : "";
  }

  const host = window.location.hostname;
  const isLocalHost =
    host === "localhost" || host === "127.0.0.1" || host === "0.0.0.0";

  if (configuredBase.length > 0) {
    try {
      const parsedConfigured = new URL(configuredBase);
      const configuredIsLocal = isLocalHostname(parsedConfigured.hostname);

      // Safety guard: prevent production hosts from calling localhost APIs.
      if (!isLocalHost && configuredIsLocal) {
        console.warn(
          "VITE_API_BASE_URL points to localhost on a non-local host. Falling back to same-origin /api.",
        );
      } else {
        return ensureApiPrefix(configuredBase);
      }
    } catch {
      // If the env value is malformed, continue to runtime fallback.
      console.warn(
        "VITE_API_BASE_URL is invalid. Falling back to runtime API base resolution.",
      );
    }
  }

  if (isLocalHost) {
    return "http://localhost:5050/api";
  }

  return `${window.location.origin.replace(/\/+$/, "")}/api`;
};

export const API_BASE_URL = resolveApiBaseUrl();

type RequestOptions = RequestInit & {
  skipRefresh?: boolean;
};

/** One invalid input, as returned by the server's validation pipe. */
export type FieldError = {
  /** Dot/bracket path into the submitted payload, e.g. `contacts[0].value`. */
  path: string;
  message: string;
};

type ParsedErrorPayload = {
  message: string;
  error?: string;
  requestId?: string;
  code?: string;
  fields?: FieldError[];
};

export class ApiError extends Error {
  readonly status: number;
  readonly statusText: string;
  readonly path: string;
  readonly requestId?: string;
  readonly errorType?: string;
  /** Machine-readable reason, e.g. `VALIDATION_FAILED`. */
  readonly code?: string;
  /** Per-input failures, present when `code === "VALIDATION_FAILED"`. */
  readonly fields?: FieldError[];

  constructor(params: {
    message: string;
    status: number;
    statusText: string;
    path: string;
    requestId?: string;
    errorType?: string;
    code?: string;
    fields?: FieldError[];
  }) {
    super(params.message);
    this.name = "ApiError";
    this.status = params.status;
    this.statusText = params.statusText;
    this.path = params.path;
    this.requestId = params.requestId;
    this.errorType = params.errorType;
    this.code = params.code;
    this.fields = params.fields;
  }

  /** True when the server rejected specific inputs rather than the request. */
  get isValidationError(): boolean {
    return this.code === "VALIDATION_FAILED" && (this.fields?.length ?? 0) > 0;
  }
}

/**
 * Raised when the session is gone and a refresh could not recover it.
 *
 * An `ApiError` subclass so pollers can classify it with the same checks as a
 * server-sent 401; the message is unchanged from when this was a bare `Error`
 * because some callers still match on it.
 */
export class SessionExpiredError extends ApiError {
  constructor(path: string) {
    super({
      message: "Session expired. Please login again.",
      status: 401,
      statusText: "Unauthorized",
      path,
    });
    this.name = "SessionExpiredError";
  }
}

/**
 * True for failures that will keep failing no matter how often they are
 * retried: the session is gone (401), the caller is not allowed (403), or the
 * route does not exist (404).
 *
 * Anything on a timer must check this and stop, otherwise a signed-out tab
 * left open all day hammers the API on every tick forever.
 */
export function isUnrecoverableApiError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403 || error.status === 404)
  );
}

/**
 * True when the request never reached a working backend — the fetch itself
 * rejected (offline, DNS, CORS) or the server returned 5xx. Distinct from a
 * 401, which means the backend answered and rejected us.
 */
export function isBackendUnreachable(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500;
  return true;
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

/**
 * Retrieve the Cognito ID token (JWT) from the current Amplify session.
 * We use the ID token (not the access token) because it contains
 * email, custom:legacy_user_id, custom:role and other user attributes
 * that the backend needs for request authorization.
 * Returns the raw JWT string or null if no Cognito session exists.
 */
async function getCognitoAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ?? null;
  } catch {
    return null;
  }
}

const LOCAL_AUTH_TOKEN_KEY = "local_auth_token";

export const setLocalAuthToken = (token: string) => {
  try {
    sessionStorage.setItem(LOCAL_AUTH_TOKEN_KEY, token);
    localStorage.setItem("rx_session_hint", "1");
  } catch {
    // Ignore storage quota / restricted errors
  }
};

export const getLocalAuthToken = (): string | null => {
  try {
    return sessionStorage.getItem(LOCAL_AUTH_TOKEN_KEY);
  } catch {
    return null;
  }
};

export const clearLocalAuthToken = () => {
  try {
    sessionStorage.removeItem(LOCAL_AUTH_TOKEN_KEY);
    localStorage.removeItem("rx_session_hint");
  } catch {
    // Ignore storage errors
  }
};

/**
 * The API has no geo-aware CDN in front of it, so the browser's timezone is
 * the server's only signal for LKR-vs-USD pricing. Resolved once per page.
 */
const CLIENT_TIMEZONE: string = (() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? "";
  } catch {
    return "";
  }
})();

function isPublicRoutePath(pathname: string): boolean {
  if (!pathname) return false;
  return (
    pathname === "/" ||
    pathname === "/login" ||
    pathname === "/register" ||
    pathname === "/2fa-verify" ||
    pathname === "/verify-email" ||
    pathname === "/forgot-password" ||
    pathname === "/reset-password" ||
    pathname === "/privacy-policy" ||
    pathname === "/terms-and-conditions" ||
    pathname === "/refund-policy" ||
    pathname === "/privacy" ||
    pathname === "/terms" ||
    pathname === "/terms-of-service" ||
    pathname === "/pricing" ||
    pathname.startsWith("/p/") ||
    pathname === "/blog" ||
    pathname.startsWith("/blog/")
  );
}

function redirectToLogin() {
  clearLocalAuthToken();
  if (
    typeof window !== "undefined" &&
    isPublicRoutePath(window.location.pathname)
  ) {
    return;
  }

  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
}

/**
 * Attempt to refresh the access token
 */
async function attemptRefresh(): Promise<boolean> {
  // If already refreshing, wait for that to complete
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        clearLocalAuthToken();
        return false;
      }

      try {
        const data = (await response.json()) as {
          tokens?: { accessToken?: string };
        };
        if (data?.tokens?.accessToken) {
          setLocalAuthToken(data.tokens.accessToken);
        }
      } catch {
        // Response body might be empty or non-JSON
      }

      return true;
    } catch {
      clearLocalAuthToken();
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/**
 * Parse error message from response
 */
async function parseErrorPayload(
  response: Response,
): Promise<ParsedErrorPayload> {
  const contentType = response.headers.get("content-type") || "";
  const requestIdFromHeader =
    response.headers.get("x-request-id") ??
    response.headers.get("x-correlation-id") ??
    undefined;

  if (contentType.includes("application/json")) {
    const data = (await response.json()) as {
      message?: string | string[];
      error?: string;
      requestId?: string;
      code?: string;
      fields?: FieldError[];
    };

    const requestId = data.requestId ?? requestIdFromHeader;
    // Carried through so callers can highlight the offending inputs rather
    // than showing a bare status message.
    const structured = { code: data.code, fields: data.fields };

    if (Array.isArray(data.message)) {
      return {
        message: data.message.join(", "),
        error: data.error,
        requestId,
        ...structured,
      };
    }
    if (typeof data.message === "string") {
      return {
        message: data.message,
        error: data.error,
        requestId,
        ...structured,
      };
    }
    if (typeof data.error === "string") {
      return {
        message: data.error,
        error: data.error,
        requestId,
        ...structured,
      };
    }
  }

  const text = await response.text();
  return {
    message: text || "Request failed",
    requestId: requestIdFromHeader,
  };
}

/**
 * Auth headers for callers that need raw `fetch` rather than `apiRequest` —
 * e.g. PDF endpoints that stream a binary body. Cookie sessions work without
 * this, but Cognito sessions need the Bearer token.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  const localToken = getLocalAuthToken();
  const cognitoToken = await getCognitoAccessToken();
  const effectiveToken = localToken || cognitoToken;
  return effectiveToken ? { Authorization: `Bearer ${effectiveToken}` } : {};
}

/**
 * Enhanced fetch with automatic token refresh
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { skipRefresh, ...fetchOptions } = options;

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const isAdminRoute = normalizedPath.startsWith("/admin");

  // Prefer local verified token (e.g. admin 2FA token) over Cognito token.
  // Administrator routes strictly require local auth and reject Cognito tokens.
  const localToken = getLocalAuthToken();
  const cognitoToken = await getCognitoAccessToken();
  const effectiveToken = localToken || (isAdminRoute ? null : cognitoToken);
  const authHeaders: Record<string, string> = effectiveToken
    ? { Authorization: `Bearer ${effectiveToken}` }
    : {};

  // Check if we are uploading a file (FormData)
  const isFormData = fetchOptions.body instanceof FormData;

  // Merge headers safely
  const headers: Record<string, string> = {
    ...authHeaders,
    ...(CLIENT_TIMEZONE ? { "x-client-timezone": CLIENT_TIMEZONE } : {}),
    ...((fetchOptions.headers as Record<string, string>) ?? {}),
  };

  // If it's NOT a file upload and no Content-Type is set, default to JSON
  if (!isFormData && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  // If it IS a file upload, ensure Content-Type is completely deleted
  // so the browser can automatically set "multipart/form-data; boundary=..."
  if (isFormData) {
    delete headers["Content-Type"];
  }

  const defaultOptions: RequestInit = {
    credentials: "include",
    headers,
  };

  // First attempt
  let response = await fetch(`${API_BASE_URL}${path}`, {
    ...defaultOptions,
    ...fetchOptions,
  });

  const isAuthRoute = normalizedPath.startsWith("/auth/");
  const isAuthRefreshRoute = normalizedPath === "/auth/refresh";
  const isAuthPublicRoute =
    normalizedPath === "/auth/login" ||
    normalizedPath === "/auth/register" ||
    normalizedPath === "/auth/bootstrap-admin";
  const isAuthOAuthRoute = normalizedPath.startsWith("/auth/google");
  const isAuthProtectedRouteEligibleForRefresh =
    normalizedPath === "/auth/me" ||
    normalizedPath === "/auth/logout" ||
    normalizedPath === "/auth/profile" ||
    normalizedPath === "/auth/change-password";
  const isPublicPageRequest =
    typeof window !== "undefined" &&
    isPublicRoutePath(window.location.pathname);

  // If 401 and not already a refresh request, try to refresh token
  const shouldAttemptRefresh =
    response.status === 401 &&
    !skipRefresh &&
    (!isPublicPageRequest || normalizedPath === "/auth/me" || normalizedPath === "/auth/logout") &&
    !isAuthRefreshRoute &&
    !isAuthPublicRoute &&
    !isAuthOAuthRoute &&
    (!isAuthRoute || isAuthProtectedRouteEligibleForRefresh);

  if (shouldAttemptRefresh) {
    // If the user has a valid Cognito session the 401 is not a session-expiry
    // problem — it means the backend couldn't verify the token (e.g. legacy
    // endpoint). Do NOT redirect; just let the error propagate instead.
    if (cognitoToken && !localToken && !isAdminRoute) {
      // Already sent the Bearer token — nothing more we can do.
      // Fall through to the normal error handler below.
    } else {
      const refreshed = await attemptRefresh();

      if (refreshed) {
        // Retry original request with new token
        const refreshedToken = getLocalAuthToken();
        const retryHeaders = { ...headers };
        if (refreshedToken) retryHeaders.Authorization = `Bearer ${refreshedToken}`;
        response = await fetch(`${API_BASE_URL}${path}`, {
          ...defaultOptions,
          ...fetchOptions,
          headers: retryHeaders,
        });

        if (response.status === 401) {
          redirectToLogin();
          throw new SessionExpiredError(path);
        }
      } else {
        // Refresh failed - redirect to login
        redirectToLogin();
        throw new SessionExpiredError(path);
      }
    }
  }

  // Handle errors
  if (!response.ok) {
    const parsed = await parseErrorPayload(response);
    const statusLabel = `${response.status} ${response.statusText}`.trim();
    const requestIdSuffix = parsed.requestId
      ? ` [requestId: ${parsed.requestId}]`
      : "";
    const finalMessage = parsed.message
      ? `${statusLabel}: ${parsed.message}${requestIdSuffix}`
      : `${statusLabel}${requestIdSuffix}`;

    // If server rejected with ADMIN_2FA_REQUIRED, the current session cannot access admin resources
    if (response.status === 403 && parsed.code === "ADMIN_2FA_REQUIRED") {
      clearLocalAuthToken();
      redirectToLogin();
    }

    // Only log errors in dev, but suppress expected 401s on /auth/me
    if (
      import.meta.env.DEV &&
      !(response.status === 401 && path === "/auth/me")
    ) {
      console.error("[API] Request failed", {
        path,
        status: response.status,
        statusText: response.statusText,
        requestId: parsed.requestId,
        message: parsed.message,
        errorType: parsed.error,
      });
    }

    throw new ApiError({
      message: finalMessage,
      status: response.status,
      statusText: response.statusText,
      path,
      requestId: parsed.requestId,
      errorType: parsed.error,
      code: parsed.code,
      fields: parsed.fields,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    return response.json() as Promise<T>;
  }

  const text = await response.text();
  return text as T;
}
